import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '../ui/drawer';
import { Button } from '../ui/button';
import ModalCloseButton from '../generic/ModalCloseButton';
import { cn } from '../../lib/utils';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return isMobile;
}

/**
 * Test-ui style layout: header / body / footer (react-bootstrap Modal shape).
 * @param {'dialog' | 'sheet'} variant
 */
function ScheduleChangePanel({
  title,
  description,
  isSubmitting,
  onClose,
  onConfirm,
  confirmLabel,
  variant = 'dialog',
}) {
  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden bg-[var(--q3-surface-default)]',
        variant === 'dialog' && 'rounded-xl',
        variant === 'sheet' && 'max-h-[inherit] rounded-none',
      )}
    >
      <div className="shrink-0 border-b border-[var(--q3-stroke-normal)] px-4 py-3 sm:px-6">
        <h2 className="text-lg font-semibold leading-snug text-[var(--q3-neutral-default)]">{title}</h2>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 text-sm leading-relaxed text-[var(--q3-neutral-light-v3)] sm:px-6">
        <p>{description}</p>
        <p className="mt-3 font-medium text-[var(--q3-danger-normal)]">
          NOTE: Schedule can be set only once, please confirm carefully.
        </p>
      </div>
      <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-[var(--q3-stroke-normal)] px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
        <Button type="button" variant="outline" disabled={isSubmitting} onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={isSubmitting}
          onClick={onConfirm}
          className={cn(
            '!bg-[var(--q3-accent-normal)] !text-[var(--q3-neutral-default_inverted)] hover:!bg-[var(--q3-accent-dark)]',
          )}
        >
          {isSubmitting ? 'Saving…' : confirmLabel}
        </Button>
      </div>
    </div>
  );
}

/**
 * Confirms pack / schedule switch from the dashboard pack switch flow.
 * Large screens: centered modal. Small screens: bottom sheet.
 * @param {'keep' | 'switch'} mode
 */
export function ConfirmScheduleChangeModal({
  open,
  onOpenChange,
  mode,
  targetTitle,
  isSubmitting,
  onConfirm,
  confirmLabel,
}) {
  const isMobile = useIsMobile();

  const handleOpenChange = (next) => {
    if (isSubmitting && !next) return;
    onOpenChange(next);
  };

  const title = 'Confirm your schedule';
  const description = `You've selected ${targetTitle} as your test schedule. All your tests will follow this calendar going forward.`;

  const handleConfirm = async () => {
    await onConfirm();
  };

  const onClose = () => handleOpenChange(false);

  const panelProps = {
    title,
    description,
    isSubmitting,
    onClose,
    onConfirm: handleConfirm,
    confirmLabel,
  };

  if (isMobile) {
    return (
      <Drawer
        shouldScaleBackground
        direction="bottom"
        open={open}
        onOpenChange={handleOpenChange}
        dismissible={!isSubmitting}
      >
        <DrawerContent className="max-h-[min(92dvh,100%)] overflow-visible rounded-t-2xl border-none bg-transparent p-0">
          <DrawerHeader className="sr-only">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          <div className="relative">
            {!isSubmitting ? <ModalCloseButton onClick={onClose} /> : null}
            <div className="overflow-hidden rounded-t-2xl border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] shadow-lg">
              <ScheduleChangePanel {...panelProps} variant="sheet" />
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[min(90vh,calc(100%-2rem))] max-w-[calc(100%-2rem)] gap-0 overflow-visible border-0 bg-transparent p-0 shadow-none sm:max-w-lg"
      >
        <div className="relative">
          {!isSubmitting ? <ModalCloseButton onClick={onClose} /> : null}
          <div className="overflow-hidden rounded-xl border border-[var(--q3-stroke-normal)] shadow-lg">
            <ScheduleChangePanel {...panelProps} variant="dialog" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
